import React, { useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import CoursesPage from './CoursesPage';
import CourseAccessDenied from './CourseAccessDenied';
import CourseContent from './CourseContent';
import LessonViewer from './LessonViewer';
import Footer from './Footer';
import GlobalHeader from './GlobalHeader';
import { hasCourseAccess, getUserCourses } from '../utils/emailEncoder';

// Type declarations for Google OAuth global objects
declare global {
  interface Window {
    google?: {
      accounts?: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
    gapi?: any;
  }
}

interface User {
  name: string;
  email: string;
  picture?: string;
}

interface DecodedToken {
  name: string;
  email: string;
  picture?: string;
}

type PageState = 'login' | 'courses' | 'course-access-denied' | 'course-content' | 'lesson' | 'loading';

export default function HomePage() {
  const [user, setUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState<PageState>('courses');
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [userCourses, setUserCourses] = useState<string[]>([]);
  const [lessonParams, setLessonParams] = useState<{ courseId: string; filename: string } | null>(null);
  const [currentHash, setCurrentHash] = useState(window.location.hash);

  // Parse course slug from URL hash (e.g., #/courses/playwright-k17)
  const parseHash = (): string | null => {
    const hash = window.location.hash;
    const match = hash.match(/^#\/courses\/(.+)$/);
    return match ? match[1] : null;
  };

  // Parse lesson from URL hash (e.g., #/lesson/playwright-k17/LESSON_LOGIN_FORM.HTML)
  const parseLessonHash = (): { courseId: string; filename: string } | null => {
    const hash = window.location.hash;
    const match = hash.match(/^#\/lesson\/([^/]+)\/(.+)$/);
    if (match) {
      return { courseId: match[1], filename: match[2] };
    }
    return null;
  };

  // Set URL hash for course
  const setCourseHash = (courseId: string | null) => {
    if (courseId) {
      window.location.hash = `#/courses/${courseId}`;
    } else {
      window.location.hash = '#/courses';
    }
  };

  // Determine active tab from URL hash
  const getActiveTab = (): 'courses' | 'certified-testers' | 'testimonials' => {
    const hash = window.location.hash;
    if (hash === '#/testimonials') {
      return 'testimonials';
    }
    if (hash === '#/certified-testers' || hash.startsWith('#/certified-testers/')) {
      return 'certified-testers';
    }
    return 'courses';
  };

  // Fetch course data to check course type
  const getCourseType = async (courseId: string): Promise<'video' | 'zoom-recorded' | null> => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${API_BASE_URL}/api/courses`);
      const data = await response.json();
      const course = data.courses?.find((c: any) => c.id === courseId);
      return course?.type || null;
    } catch (error) {
      console.error('Error fetching course data:', error);
      return null;
    }
  };

  // Save user session to localStorage
  const saveUserSession = (userData: User, page: PageState, course?: string, courses?: string[]) => {
    const sessionData = {
      user: userData,
      currentPage: page,
      selectedCourse: course || '',
      userCourses: courses || [],
      timestamp: Date.now()
    };
    localStorage.setItem('userSession', JSON.stringify(sessionData));
  };

  // Restore user session from localStorage
  const restoreUserSession = async () => {
    try {
      const sessionData = localStorage.getItem('userSession');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        const { user: savedUser, currentPage: savedPage, selectedCourse: savedCourse, userCourses: savedCourses } = parsed;

        // Check if session is not too old (24 hours)
        const isSessionValid = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;

        if (isSessionValid && savedUser) {
          setUser(savedUser);
          setUserCourses(savedCourses || []);

          // If user was in a specific course, restore that state
          if (savedPage === 'course-content' && savedCourse) {
            setCurrentPage('course-content');
            setSelectedCourse(savedCourse);
          } else {
            // Otherwise, show courses page
            setCurrentPage('courses');
          }

          return true;
        } else {
          // Session expired, clear it
          localStorage.removeItem('userSession');
        }
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      localStorage.removeItem('userSession');
    }
    return false;
  };

  // Check for existing session and hash on component mount
  React.useEffect(() => {
    const checkSession = async () => {
      const sessionRestored = await restoreUserSession();
      setIsLoading(false);

      // Check URL hash for lesson first
      const lessonParams = parseLessonHash();
      if (lessonParams) {
        setLessonParams(lessonParams);
        setCurrentPage('lesson');
        return;
      }

      // Check URL hash for course slug
      const courseSlug = parseHash();
      if (courseSlug) {
        setSelectedCourse(courseSlug);
        // Get current user from session (restoreUserSession sets user state, but we check localStorage for immediate access)
        const sessionData = localStorage.getItem('userSession');
        const currentUser = sessionData ? JSON.parse(sessionData).user : null;
        if (currentUser) {
          const hasAccess = await hasCourseAccess(currentUser.email, courseSlug);
          if (hasAccess) {
            setCurrentPage('course-content');
            saveUserSession(currentUser, 'course-content', courseSlug);
          } else {
            // Check if it's a video course - if so, show course detail (with buy button)
            // Only show access denied for zoom-recorded courses
            const courseType = await getCourseType(courseSlug);
            if (courseType === 'video') {
              setCurrentPage('courses');
              saveUserSession(currentUser, 'courses', courseSlug);
            } else {
              setCurrentPage('course-access-denied');
              saveUserSession(currentUser, 'course-access-denied', courseSlug);
            }
          }
        } else {
          // User not logged in, show courses page with course selected for details
          setCurrentPage('courses');
        }
      } else if (!sessionRestored) {
        setCurrentPage('courses');
      }
    };

    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for hash changes (browser back/forward)
  React.useEffect(() => {
    const handleHashChange = async () => {
      // Update currentHash to trigger re-render for activeTab
      setCurrentHash(window.location.hash);

      // Check for lesson route first
      const lessonParams = parseLessonHash();
      if (lessonParams) {
        setLessonParams(lessonParams);
        setCurrentPage('lesson');
        return;
      }

      const courseSlug = parseHash();
      if (courseSlug) {
        setSelectedCourse(courseSlug);
        if (user) {
          const hasAccess = await hasCourseAccess(user.email, courseSlug);
          if (hasAccess) {
            setCurrentPage('course-content');
            saveUserSession(user, 'course-content', courseSlug);
          } else {
            // Check if it's a video course - if so, show course detail (with buy button)
            // Only show access denied for zoom-recorded courses
            const courseType = await getCourseType(courseSlug);
            if (courseType === 'video') {
              setCurrentPage('courses');
              saveUserSession(user, 'courses', courseSlug, userCourses);
            } else {
              setCurrentPage('course-access-denied');
              saveUserSession(user, 'course-access-denied', courseSlug);
            }
          }
        } else {
          setCurrentPage('courses');
        }
      } else {
        setSelectedCourse('');
        setLessonParams(null);
        setCurrentPage('courses');
        if (user) {
          saveUserSession(user, 'courses', undefined, userCourses);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [user, userCourses]);

  const handleSignOut = () => {
    // Use Google's logout function
    if (window.google && window.google.accounts) {
      window.google.accounts.id.disableAutoSelect();
    }

    // Clear session from localStorage
    localStorage.removeItem('userSession');

    // Reset local state
    setUser(null);
    setCurrentPage('courses');
    setSelectedCourse('');
  };

  const handleSignIn = () => {
    if (window.google?.accounts?.id) {
      // Initialize Google Identity Services for button-based authentication
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '890528838839-7vbp03b6gmfi7u40of3098mvftqr6qh1.apps.googleusercontent.com',
        callback: handleLoginSuccess,
        auto_select: false,
        cancel_on_tap_outside: true
      });

      // Create a hidden container for the Google Sign-In button
      const hiddenContainer = document.createElement('div');
      hiddenContainer.style.position = 'absolute';
      hiddenContainer.style.left = '-9999px';
      hiddenContainer.style.top = '-9999px';
      hiddenContainer.style.visibility = 'hidden';
      document.body.appendChild(hiddenContainer);

      // Render the Google Sign-In button
      window.google.accounts.id.renderButton(hiddenContainer, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: '250'
      });

      // Auto-click the button after a short delay to ensure it's rendered
      setTimeout(() => {
        const googleButton = hiddenContainer.querySelector('div[role="button"]') as HTMLElement;
        if (googleButton) {
          googleButton.click();
        }

        // Clean up the hidden container
        setTimeout(() => {
          if (document.body.contains(hiddenContainer)) {
            document.body.removeChild(hiddenContainer);
          }
        }, 1000);
      }, 100);
    }
  };

  const handleLoginSuccess = async (credentialResponse: any) => {
    if (credentialResponse.credential) {
      // Show loading state immediately
      setIsLoading(true);
      setCurrentPage('loading');

      try {
        const decoded = jwtDecode(credentialResponse.credential) as DecodedToken;

        const userData: User = {
          name: decoded.name,
          email: decoded.email,
          picture: decoded.picture
        };

        // Fetch user courses
        const courses = await getUserCourses(userData.email);

        setUser(userData);
        setUserCourses(courses);

        // Check if there's a course slug in the URL hash
        const courseSlug = parseHash();
        if (courseSlug) {
          const hasAccess = await hasCourseAccess(userData.email, courseSlug);
          if (hasAccess) {
            setSelectedCourse(courseSlug);
            setCurrentPage('course-content');
            saveUserSession(userData, 'course-content', courseSlug, courses);
          } else {
            // Check if it's a video course - if so, show course detail (with buy button)
            // Only show access denied for zoom-recorded courses
            const courseType = await getCourseType(courseSlug);
            if (courseType === 'video') {
              setSelectedCourse(courseSlug);
              setCurrentPage('courses');
              saveUserSession(userData, 'courses', courseSlug, courses);
            } else {
              setSelectedCourse(courseSlug);
              setCurrentPage('course-access-denied');
              saveUserSession(userData, 'course-access-denied', courseSlug, courses);
            }
          }
        } else {
          setCurrentPage('courses');
          saveUserSession(userData, 'courses', undefined, courses);
        }
      } catch (error) {
        console.error('Login error:', error);
        setCurrentPage('courses');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCourseSelect = async (courseId: string) => {
    if (!user) return;

    setCourseHash(courseId);
    const hasAccess = await hasCourseAccess(user.email, courseId);
    if (hasAccess) {
      setSelectedCourse(courseId);
      setCurrentPage('course-content');
      saveUserSession(user, 'course-content', courseId);
    } else {
      // Check if it's a video course - if so, show course detail (with buy button)
      // Only show access denied for zoom-recorded courses
      const courseType = await getCourseType(courseId);
      if (courseType === 'video') {
        setSelectedCourse(courseId);
        setCurrentPage('courses');
        saveUserSession(user, 'courses', courseId, userCourses);
      } else {
        setSelectedCourse(courseId);
        setCurrentPage('course-access-denied');
        saveUserSession(user, 'course-access-denied', courseId);
      }
    }
  };

  const handleReturnToCourses = () => {
    // Return to courses list but preserve selected course so details can open
    setCurrentPage('courses');
    // Keep hash if course is selected, otherwise clear it
    if (!selectedCourse) {
      setCourseHash(null);
    }
    if (user) {
      saveUserSession(user, 'courses', selectedCourse || undefined, userCourses);
    }
  };

  const getCourseName = (courseId: string): string => {
    const courseNames: { [key: string]: string } = {
      'playwright-k17': 'Playwright - K17',
      'playwright-k18': 'Playwright - K18',
      'playwright-k16': 'Playwright - K16'
    };
    return courseNames[courseId] || courseId;
  };

  // Show loading while checking for existing session
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '48px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          textAlign: 'center',
          maxWidth: '320px',
          width: '100%'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            borderRadius: '50%',
            margin: '0 auto 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              borderTop: '3px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
          <h2 style={{
            margin: '0 0 8px 0',
            color: '#1f2937',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            Loading...
          </h2>
          <p style={{
            margin: 0,
            color: '#6b7280',
            fontSize: '14px'
          }}>
            Checking your session
          </p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  // Show loading page
  if (currentPage === 'loading') {
    return (
      <div>
        <GlobalHeader user={user} onSignOut={handleSignOut} onSignIn={handleSignIn} />
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '80px 20px 20px 20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Animated spinner */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 24px',
              position: 'relative'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '32px',
                height: '32px',
                border: '3px solid #f3f4f6',
                borderTop: '3px solid #667eea',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite reverse'
              }} />
            </div>

            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 12px 0'
            }}>
              Signing you in...
            </h2>

            <p style={{
              fontSize: '1rem',
              color: '#6b7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              Please wait while we verify your account and load your courses.
            </p>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: '4px',
              backgroundColor: '#e5e7eb',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s ease-in-out infinite',
                borderRadius: '2px'
              }} />
            </div>

            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes shimmer {
                0% { background-position: -200% 0; }
                100% { background-position: 200% 0; }
              }
            `}</style>
          </div>
        </div>
      </div>
    );
  }

  // Show courses page
  if (currentPage === 'courses') {
    return (
      <div>
        <GlobalHeader user={user} onSignOut={handleSignOut} onSignIn={handleSignIn} activeTab={getActiveTab()} />
        <div style={{ paddingTop: '80px' }}>
          <CoursesPage
            user={user}
            onSignOut={handleSignOut}
            onCourseSelect={handleCourseSelect}
            onSignIn={handleSignIn}
            initialSelectedCourseId={selectedCourse || undefined}
          />
          <Footer userCourses={userCourses} />
        </div>
      </div>
    );
  }

  // Show course content
  if (currentPage === 'course-content' && selectedCourse && user) {
    return (
      <div>
        <GlobalHeader user={user} onSignOut={handleSignOut} onSignIn={handleSignIn} activeTab={getActiveTab()} />
        <div style={{ paddingTop: '80px' }}>
          <CourseContent
            courseId={selectedCourse}
            courseName={getCourseName(selectedCourse)}
            user={user}
            onSignOut={handleSignOut}
            onBackToCourses={handleReturnToCourses}
          />
          <Footer userCourses={userCourses} />
        </div>
      </div>
    );
  }

  // Show lesson viewer
  if (currentPage === 'lesson' && lessonParams) {
    return (
      <LessonViewer
        courseId={lessonParams.courseId}
        filename={lessonParams.filename}
        user={user}
        onSignOut={handleSignOut}
        onSignIn={handleSignIn}
      />
    );
  }

  // Show course access denied
  if (currentPage === 'course-access-denied' && user) {
    return (
      <div>
        <GlobalHeader user={user} onSignOut={handleSignOut} onSignIn={handleSignIn} activeTab={getActiveTab()} />
        <div style={{ paddingTop: '80px' }}>
          <CourseAccessDenied
            user={user}
            courseName={selectedCourse ? getCourseName(selectedCourse) : 'Selected Course'}
            onSignOut={handleSignOut}
            onReturnToCourses={handleReturnToCourses}
          />
          <Footer userCourses={userCourses} />
        </div>
      </div>
    );
  }

  // Default: Show courses page (for both authenticated and non-authenticated users)
  return (
    <div>
      <GlobalHeader user={user} onSignOut={handleSignOut} onSignIn={handleSignIn} activeTab={getActiveTab()} />
      <div style={{ paddingTop: '80px' }}>
        <CoursesPage
          user={user}
          onSignOut={handleSignOut}
          onCourseSelect={handleCourseSelect}
          onSignIn={handleSignIn}
        />
        <Footer userCourses={userCourses} />
      </div>
    </div>
  );
}